/**
 * 鳥取県の背景ツアー「鳥取めぐり」コンテンツ。
 *
 * 軸は一日の時間。夜明け前の境港から、夜の三朝温泉へ。
 *
 * 大山について。
 *  気象庁の活火山一覧に大山は入っていない（中国地方の活火山は三瓶山と阿武火山群のみ）。
 *  「活火山」とは書かない。環境省の表現どおり「成層火山の上に溶岩円頂丘がのった火山」にとどめる。
 *
 * 境港について。
 *  水木しげる関連は著作権のため一切扱わない。水産都市・ズワイガニの水揚げ港としてのみ描く。
 *
 * 三徳山三佛寺 投入堂について。
 *  受付時間・下山時刻・冬季に入れない期間は年によって変わるので書かない。
 *  「単独では入山できない」「靴の条件がある」という変わらない決まりだけを書き、
 *  出かける前に寺の最新の発表を確かめる、という形にする。
 *  「日本一危ない国宝鑑賞」は三佛寺公式の表現ではない。使わない。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 鳥取砂丘の「日本最大の砂丘」は国・県の断定形が確認できない。観光協会は「日本最大級」と級付き。
 *  - 三朝温泉の健康効果（ホルミシス効果など）は断定しない。「ラドンを含む放射能泉」という泉質の事実のみ。
 *  - 「世界屈指の放射能泉」も比較の断定なので使わない。
 *  - 大山の牛馬市の「日本最大」は、日本遺産のストーリー名としての引用だけに使う。地の文で断定しない。
 *  - 「因幡の白うさぎ」のキャラクター化された意匠には触れない。白兎海岸は砂浜と岩礁で描く。
 *  - 浦富海岸の国の名勝・天然記念物指定は出典が取れていない。書かない。
 */

import type { StageTour } from '../types';

export const tottoriSceneryTour: StageTour = {
  scenes: [
    {
      id: 'sakaiminato',
      name: '境港',
      area: '境港市・夜明け前',
      image: '/assets/scenery/tottori/scenes/01_sakaiminato.webp',
      hotspots: [
        {
          id: 'sakaiminato-zuwai',
          x: 0.46,
          y: 0.42,
          title: 'ズワイガニが揚がる港',
          body: '鳥取県はズワイガニの水揚げ状況を漁期ごとにまとめて公表しています。令和4年漁期の資料では、田後漁協と、鳥取県漁協の網代港・賀露・境港の各支所での水揚げが集計されています。',
          source: 'https://www.pref.tottori.lg.jp/secure/290939/besshi_r5_all.pdf',
          mapQuery: '境港 漁港',
        },
        {
          id: 'sakaiminato-eisei',
          x: 0.72,
          y: 0.5,
          title: '高度衛生管理の基本計画',
          body: '境港地区の高度衛生管理基本計画は、平成26年9月10日に水産庁が公表した計画です。全国有数の水産物の流通拠点である特定第3種漁港に、国が目指す高度な衛生管理を導入するためのもので、事業期間は平成26年度から令和5年度までの10年間でした。',
          source: 'https://www.pref.tottori.lg.jp/240213.htm',
          mapQuery: '境港 高度衛生管理型市場',
        },
        {
          id: 'sakaiminato-hama',
          x: 0.22,
          y: 0.5,
          title: '浜の活力再生プラン',
          body: '浜の活力再生プランは、水産庁の指導のもと全国各地の浜で策定されている計画です。鳥取県でも平成26年度に4地域で水産業再生委員会が設けられ、漁業者が将来のあるべき姿や取り組むべき課題を地域全体で考えてプランにまとめています。令和6年度からは第3期が始まっています。',
          source: 'https://www.pref.tottori.lg.jp/243586.htm',
          mapQuery: '境港市昭和町',
        },
      ],
    },
    {
      id: 'tottori-sakyu',
      name: '鳥取砂丘 馬の背',
      area: '鳥取市・日の出',
      image: '/assets/scenery/tottori/scenes/02_tottori_sakyu.webp',
      hotspots: [
        {
          id: 'sakyu-tennen',
          x: 0.44,
          y: 0.4,
          title: '国の天然記念物の砂丘',
          body: '鳥取市が公表する国指定文化財の一覧に、「鳥取砂丘」が天然記念物（地質）として載っています。所在地は浜坂と福部町湯山です。同じ一覧には、白兎のハマナス自生南限地帯や白兎神社樹叢も天然記念物として並んでいます。',
          source: 'https://www.city.tottori.lg.jp/page/6243.html',
          mapQuery: '鳥取砂丘',
        },
        {
          id: 'sakyu-umanose',
          x: 0.72,
          y: 0.34,
          title: '高さ47メートルの砂の壁',
          body: '鳥取市観光コンベンション協会は、馬の背を真下から眺めると高さおよそ47メートルの砂の壁になると説明しています。第二砂丘列と呼ばれる急斜面で、登り切ると日本海が広がります。',
          source: 'https://www.torican.jp/spot/detail_1064.html',
          mapQuery: '鳥取砂丘 馬の背',
        },
        {
          id: 'sakyu-suribachi',
          x: 0.22,
          y: 0.48,
          title: 'スリバチと火山灰の層',
          body: '山陰海岸ジオパークの解説では、鳥取砂丘では起伏の大きい明瞭な砂丘列やスリバチ地形など、砂丘地特有の景観が見られるとされています。砂丘にはおよそ5万年前の大山倉吉軽石の火山灰層がはさまり、その下が古砂丘、上が新砂丘です。',
          source: 'https://sanin-geo.jp/geosites/tottorisakyu/',
          mapQuery: '鳥取砂丘 スリバチ',
        },
      ],
    },
    {
      id: 'hakuto-kaigan',
      name: '白兎海岸',
      area: '鳥取市白兎・朝',
      image: '/assets/scenery/tottori/scenes/03_hakuto_kaigan.webp',
      hotspots: [
        {
          id: 'hakuto-hamanasu',
          x: 0.46,
          y: 0.46,
          title: 'ハマナスの自生南限地帯',
          body: '白兎海岸の東側にはハマナスの群生地があり、国の天然記念物に指定されています。毎年5月中旬から6月中旬にかけて、淡い紅色の花が咲きます。',
          source: 'https://www.torican.jp/spot/detail_1066.html',
          mapQuery: '白兎海岸 ハマナス自生南限地帯',
        },
        {
          id: 'hakuto-jusou',
          x: 0.74,
          y: 0.38,
          title: '樹叢も天然記念物',
          body: '鳥取市の国指定文化財の一覧には、ハマナス自生南限地帯とあわせて「白兎神社樹叢」も天然記念物（植物）として載っています。どちらも所在地は白兎で、海辺のハマナスと神社の森の両方が国の天然記念物です。',
          source: 'https://www.city.tottori.lg.jp/page/6243.html',
          mapQuery: '白兎神社樹叢',
        },
        {
          id: 'hakuto-yuminari',
          x: 0.22,
          y: 0.4,
          title: '弓なりに連なる白砂の浜',
          body: '鳥取市観光コンベンション協会は、白兎海岸を弓なりに連なる白砂の浜が美しい海岸と紹介しています。海岸の西側には白兎の丘と呼ばれる展望広場があり、日本海を背にした眺めが開けます。',
          source: 'https://www.torican.jp/spot/detail_1066.html',
          mapQuery: '白兎海岸',
        },
      ],
    },
    {
      id: 'uradome-kaigan',
      name: '浦富海岸 千貫松島',
      area: '岩美町・午前',
      image: '/assets/scenery/tottori/scenes/04_uradome_kaigan.webp',
      hotspots: [
        {
          id: 'uradome-granite',
          x: 0.46,
          y: 0.44,
          title: '三千万年前の花こう岩',
          body: '浦富海岸は、日本海ができる前の大陸だった時代、およそ3000万年前の花こう岩でできた岩石海岸です。日本海の荒波と冬の季節風が、花こう岩を節理や断層に沿って削り、洞門や離れ岩をつくってきました。',
          source: 'https://sanin-geo.jp/geosites/uradomekaigan/',
          mapQuery: '浦富海岸',
        },
        {
          id: 'uradome-senganmatsu',
          x: 0.72,
          y: 0.36,
          title: '洞門をもつ千貫松島',
          body: '千貫松島は、花こう岩が節理に沿って侵食されてできた海食洞門をもつ離れ岩です。浦富海岸には、透明度の高い海と花崗岩に生える緑の松、岩石と砂浜が交互に続く15キロの海岸線が続きます。',
          source: 'https://sanin-geo.jp/geosites/uradomekaigan/',
          mapQuery: '千貫松島',
        },
        {
          id: 'uradome-kamogaiso',
          x: 0.22,
          y: 0.5,
          title: '透明度25メートルの海',
          body: '岩美町は、おしろいの断崖から城原海岸の菜種五島にかけての海域について、岩石海岸に特有の複雑な海中景観が素晴らしく、透明度は25メートルあると紹介しています。',
          source: 'https://www.iwami.gr.jp/2467.htm',
          mapQuery: '鴨ヶ磯 城原海岸',
        },
      ],
    },
    {
      id: 'nageiredo',
      name: '三徳山三佛寺 投入堂',
      area: '三朝町・昼前',
      image: '/assets/scenery/tottori/scenes/05_nageiredo.webp',
      hotspots: [
        {
          id: 'nageiredo-heian',
          x: 0.46,
          y: 0.36,
          title: '平安後期に建てられた堂',
          body: '三佛寺によると、投入堂は平安時代後期に建てられたことが、2001年に奈良文化財研究所がおこなった年輪年代測定で判明しました。隣の納経堂も同じ時期の建立とされています。',
          source: 'https://www.mitokusan.jp/',
          mapQuery: '三徳山三佛寺 投入堂',
        },
        {
          id: 'nageiredo-hitori',
          x: 0.72,
          y: 0.52,
          title: 'ひとりでは入山できない',
          body: '投入堂へ向かう参拝路は、必ず二人以上で、両手が使えて動きやすい格好で登ることが求められます。靴にも条件があり、許可が下りないことがあります。入山できる時期と時間は変わるので、出かける前に寺の最新の発表を確かめてください。',
          source: 'https://www.mitokusan.jp/',
          mapQuery: '三佛寺 投入堂参拝受付',
        },
        {
          id: 'nageiredo-nihonisan',
          x: 0.22,
          y: 0.5,
          title: '六根清浄と六感治癒の地',
          body: '三徳山と三朝温泉は、平成27年度の第一回認定で日本遺産に選ばれました。ストーリーは「六根清浄と六感治癒の地」。三徳山に参拝する前に心身を清める場所が三朝温泉だったと説明されています。',
          source: 'https://misasaonsen.jp/japan-heritage/',
          mapQuery: '三徳山 日本遺産',
        },
      ],
    },
    {
      id: 'kurayoshi',
      name: '倉吉白壁土蔵群',
      area: '倉吉市・昼',
      image: '/assets/scenery/tottori/scenes/06_kurayoshi.webp',
      hotspots: [
        {
          id: 'kurayoshi-judenken',
          x: 0.46,
          y: 0.42,
          title: '国が選んだ商家町',
          body: '倉吉市打吹玉川は、文化庁の重要伝統的建造物群保存地区一覧に商家町として載っています。選定は平成10年12月25日、面積は9.2ヘクタール。鳥取県内では最も早く選ばれた保存地区です。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '倉吉市打吹玉川伝統的建造物群保存地区',
        },
        {
          id: 'kurayoshi-shikkui',
          x: 0.74,
          y: 0.36,
          title: '白い漆喰と黒い焼き杉',
          body: '土蔵の壁は上半分が白い漆喰仕上げ、下半分が黒い焼き杉板の腰壁です。漆喰は防水の役目を果たし、焼き杉板は耐火性と風雨への耐久性を高めるために使われています。色の二段構えには理由があります。',
          source: 'https://www.kurayoshi-kankou.jp/kurayoshi_area/shirakabedozogun/',
          mapQuery: '倉吉 白壁土蔵群',
        },
        {
          id: 'kurayoshi-ishibashi',
          x: 0.22,
          y: 0.5,
          title: '玉川に架かる一枚石の橋',
          body: '玉川沿いには白壁と赤い石州瓦の蔵が並び、川には緩やかな反りをもつ一枚石の石橋が架かっています。石州瓦は焼成温度が1200度以上と高いため、凍害に強いのが特徴です。',
          source: 'https://www.kurayoshi-kankou.jp/kurayoshi_area/shirakabedozogun/',
          mapQuery: '玉川 石橋 倉吉',
        },
      ],
    },
    {
      id: 'wakasa-juku',
      name: '若桜宿 カリヤ通り',
      area: '若桜町・午後',
      image: '/assets/scenery/tottori/scenes/07_wakasa_juku.webp',
      hotspots: [
        {
          id: 'wakasa-judenken',
          x: 0.46,
          y: 0.44,
          title: '令和になって選ばれた町並み',
          body: '若桜町若桜は、文化庁の重要伝統的建造物群保存地区一覧に商家町として載っています。選定は令和3年8月2日、面積は9.5ヘクタール。鳥取県内では三つ目、もっとも新しく選ばれた保存地区です。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '若桜町若桜伝統的建造物群保存地区',
        },
        {
          id: 'wakasa-taika',
          x: 0.74,
          y: 0.38,
          title: '大火のあとに整えた通り',
          body: '若桜町の観光ガイドによると、明治18年の大火のあとに住民主導で町並みが整えられ、その姿が蔵通り、カリヤ通りとして今も残っています。最後の城主の時に完成した町割りや水路も受け継がれています。',
          source: 'https://kanko.town.wakasa.tottori.jp/feature/wakasacho/',
          mapQuery: 'カリヤ通り 若桜',
        },
        {
          id: 'wakasa-hyonosen',
          x: 0.22,
          y: 0.5,
          title: '中国地方で二番目に高い山の麓',
          body: '若桜町の東には、中国地方で二番目に高い氷ノ山がそびえます。標高は1510メートル。寛政7年編纂の「因幡誌」には、若桜が交通の要所、地方物資の集散地として発展したことが記されています。',
          source: 'https://kanko.town.wakasa.tottori.jp/feature/wakasacho/',
          mapQuery: '氷ノ山',
        },
      ],
    },
    {
      id: 'tokorogo',
      name: '所子の農村集落',
      area: '大山町・夕方前',
      image: '/assets/scenery/tottori/scenes/08_tokorogo.webp',
      hotspots: [
        {
          id: 'tokorogo-judenken',
          x: 0.46,
          y: 0.42,
          title: '農村集落の保存地区',
          body: '大山町所子は、文化庁の重要伝統的建造物群保存地区一覧に農村集落として載っています。選定は平成25年12月27日、面積は25.8ヘクタール。鳥取県内の保存地区で、種別が農村集落なのは所子だけです。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '大山町所子伝統的建造物群保存地区',
        },
        {
          id: 'tokorogo-sankeido',
          x: 0.74,
          y: 0.36,
          title: '大山参詣道が通る集落',
          body: '集落内を通る大山参詣道（坊領道）沿いには、重要文化財の門脇家住宅、県指定保護文化財の南門脇家住宅、国登録有形文化財の東門脇家住宅と美甘家住宅など、近世から昭和初期の建物が多く残っています。',
          source: 'https://www.daisen.jp/10/33/2/1/',
          mapQuery: '門脇家住宅 大山町',
        },
        {
          id: 'tokorogo-tenpo',
          x: 0.22,
          y: 0.5,
          title: '天保の絵図のままの地割',
          body: '大山町によると、所子では天保14年の絵図に描かれた地割などが良好に保たれています。集落内を流れる用水の音が町並みと相まって、伯耆地方の伝統的な農村集落としての風致をつくっています。',
          source: 'https://www.daisen.jp/10/33/2/1/',
          mapQuery: '大山町所子 集落',
        },
      ],
    },
    {
      id: 'daisen',
      name: '大山',
      area: '大山町・伯耆町・夕暮れ',
      image: '/assets/scenery/tottori/scenes/09_daisen.webp',
      hotspots: [
        {
          id: 'daisen-saikou',
          x: 0.46,
          y: 0.38,
          title: '中国地方でいちばん高い山',
          body: '環境省によると、大山は標高1729メートルの中国地方最高峰で、成層火山の上に溶岩円頂丘がのった火山です。山頂部に続く北壁と南壁の急な崖地が、荒々しい山岳景観をつくっています。',
          source: 'https://www.env.go.jp/park/daisen/point/index.html',
          mapQuery: '大山 弥山',
        },
        {
          id: 'daisen-buna',
          x: 0.74,
          y: 0.5,
          title: '西日本最大級のブナ林',
          body: '大山では標高800メートルから1300メートルにかけて、西日本最大級のブナの自然林が残っています。1300メートルより上はダイセンキャラボクをまじえた風衝低木帯です。大山隠岐国立公園の指定は昭和11年2月1日。',
          source: 'https://www.env.go.jp/park/daisen/point/index.html',
          mapQuery: '大山隠岐国立公園',
        },
        {
          id: 'daisen-gyubaichi',
          x: 0.22,
          y: 0.48,
          title: '牛馬市が開かれた山のふもと',
          body: '鳥取県は、平成28年度に認定された日本遺産のストーリー「地蔵信仰が育んだ日本最大の大山牛馬市」を紹介しています。平安時代末以降、大山の地蔵菩薩への信仰が牛馬のご加護を願う人々を大山寺に集め、参詣者や牛馬で賑わった大山道沿いには、今も石畳道や宿場の町並みが残っています。',
          source: 'https://www.pref.tottori.lg.jp/285904.htm',
          mapQuery: '大山寺 博労座',
        },
      ],
    },
    {
      id: 'misasa-onsen',
      name: '三朝温泉 三徳川の河畔',
      area: '三朝町・夜',
      image: '/assets/scenery/tottori/scenes/10_misasa_onsen.webp',
      hotspots: [
        {
          id: 'misasa-radon',
          x: 0.46,
          y: 0.44,
          title: 'ラドンを含む放射能泉',
          body: '三朝温泉観光協会によると、三朝の湯は高濃度のラドンを含む放射能泉です。ラドンはラジウムが分解されて生じるもので、三朝ではこの湯に浸かるだけでなく、吸う、飲むという楽しみ方も紹介されています。',
          source: 'https://misasaonsen.jp/about',
          mapQuery: '三朝温泉',
        },
        {
          id: 'misasa-kawarahuro',
          x: 0.72,
          y: 0.54,
          title: '三朝橋のたもとの露天風呂',
          body: '三朝橋のたもとには、三朝温泉名物の露天風呂「河原風呂」と足湯「河原の湯」があります。三朝橋は昭和9年に造られた青御影石造りの橋で、三朝大橋の名で親しまれています。',
          source: 'https://misasaonsen.jp/about',
          mapQuery: '三朝温泉 河原風呂',
        },
        {
          id: 'misasa-nihonisan',
          x: 0.22,
          y: 0.48,
          title: '山に登る前に身を清めた湯',
          body: '三朝温泉と三徳山は、平成27年度第一回の認定でひとつの日本遺産「六根清浄と六感治癒の地」に選ばれました。三徳山に参拝する前に、心身を清める場所としての拠点を担ったのが三朝温泉だったと説明されています。',
          source: 'https://misasaonsen.jp/japan-heritage/',
          mapQuery: '三朝温泉 日本遺産',
        },
      ],
    },
  ],
};
